import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Input, Modal, message } from "antd";
import * as Styles from "./styles";
import axios from "../../common/api/axios";
import { OrchestratorAPI } from "../../common/api/axios";
import InwardModal from "./components/inward-modal/InwardModal";
import LegacyInwardModal from "./components/inward-modal/LegacyInwardModal";
import { formatSlotDisplay } from "../../common/utils/slot";
import { useInwardStatusContext } from "../../common/hooks/useInwardStatus";
import {
  flattenStaticPickWallSlots,
  StaticPickWallSlot,
} from "../../common/utils/staticPickWall";
import {
  evaluateExitPrompt,
  ExitPromptReason,
  INWARD_INACTIVITY_LIMIT_MS,
} from "../../common/utils/inwardExitPrompt";
import { hasExitOrchestrationStep } from "../../common/utils/inwardOrchestration";
import {
  findStrandedInwardTasks,
  isAlreadyCompleteError,
  normalizeBinId,
} from "../../common/utils/inwardCompletion";
import { playAlertSound } from "../../common/utils/alertSound";
import {
  InwardBinLocationSubmission,
  PendingInwardOrder,
  SlotPosition,
} from "./types";

// ── Types ──

interface ProcessingQueueItem {
  processId: string;
  requestTime: string;
  binId: string;
  operatorTaskId: string;
}

// Keyed by bin, not by gate: a gate takes tote after tote, so keying the monitor by gate made a
// second submission at the same gate silently evict the first tote's completion.
interface MonitoredInwardSlot {
  binId: string;
  slotKey: string;
  operatorTaskId?: string;
  hasSeenProcessing: boolean;
  completing: boolean;
}

interface Gate {
  slot: SlotPosition;
  processingQueue: ProcessingQueueItem[];
  group: string;
  mappedPtlDeviceId: string;
  isDisabled: boolean;
  kind?: "dynamic" | "static";
  id?: string;
}

interface InwardStation {
  pickStationId: string;
  mappedPutWallId: string;
  gates: Gate[];
  purpose: number;
  originalPurpose: number;
}

interface ResolvedSku {
  skuId: string;
  isStatic: boolean;
  inductQuantity: string;
}

type SlotVisualState = "enabled" | "processing" | "done";

export interface InwardProps {
  taskCount: any;
  taskDetails: any;
  configName?: string;
}

function slotKey(pos: SlotPosition): string {
  return `${pos.x}.${pos.y}.${pos.z}`;
}


const NOT_STORED_WARN_THRESHOLD = 40;

export default function MaterialInward({
  taskCount,
  taskDetails,
  configName,
}: InwardProps) {
  const [stations, setStations] = useState<InwardStation[]>([]);
  const [loading, setLoading] = useState(true);

  // Inward runs either as its own orchestration or in flight inside a fulfillment run. The
  // orchestrator is polled once for the whole app so this screen and the nav lock agree.
  const {
    inwardActive,
    isInflightInward,
    orchestrationId,
    loading: orchestratorLoading,
    refresh: refreshInwardStatus,
  } = useInwardStatusContext();
  const [exitingInward, setExitingInward] = useState(false);
  // Server truth for "an exit was already requested for this orchestration". Local
  // `exitingInward` is only an optimistic fast path — it dies with the component, so on
  // its own it re-enables the button whenever the operator leaves the tab and returns.
  const [exitRequested, setExitRequested] = useState(false);

  const [selectedGate, setSelectedGate] = useState<Gate | null>(null);
  const [staticSlots, setStaticSlots] = useState<StaticPickWallSlot[]>([]);
  const [scanSkuId, setScanSkuId] = useState("");
  const [resolvedSku, setResolvedSku] = useState<ResolvedSku | null>(null);
  const [skuLookupLoading, setSkuLookupLoading] = useState(false);
  const [skuLookupError, setSkuLookupError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [availableForStorage, setAvailableForStorage] = useState<number>(0);
  const [pendingOrders, setPendingOrders] = useState<PendingInwardOrder[]>([]);
  const [pendingOrdersLoading, setPendingOrdersLoading] = useState(false);
  const [pendingOrdersError, setPendingOrdersError] = useState<string | null>(
    null
  );

  // Local slot state overrides
  // - After submit: key → "processing"
  // - After poll detects completion: key → "done" (for 3 seconds)
  // - After 3 seconds: key removed (falls through to API-derived state)
  const [localOverrides, setLocalOverrides] = useState<
    Record<string, SlotVisualState>
  >({});

  // Number of dynamic inwards the bot is still processing (submitted, not yet stored).
  const [inFlightCount, setInFlightCount] = useState(0);
  const [exitPromptOpen, setExitPromptOpen] = useState(false);
  const [exitPromptReason, setExitPromptReason] =
    useState<ExitPromptReason | null>(null);

  // Bin id → monitored inward state for the tote submitted at a gate. Completion needs
  // positive evidence the store happened: either the poll saw that exact bin in the gate
  // queue and then saw it leave, or the server reports the bin slot-assigned. `hasSeenProcessing`
  // is now only the fast path for the first case — it is no longer a precondition, because a
  // gate visit can be shorter than the poll interval and go unobserved entirely.
  const monitoredSlotsRef = useRef<Map<string, MonitoredInwardSlot>>(
    new Map()
  );
  // Guards for the server-side reconciliation sweep: one pass at a time, and never two
  // completions in flight for the same task. The station snapshot is mirrored into a ref so the
  // sweep can read it without taking a dependency on it — the sweep refreshes `stations` itself,
  // which would otherwise retrigger the sweep on its own write.
  const reconcilingRef = useRef(false);
  const reconcileInFlightRef = useRef<Set<string>>(new Set());
  const stationsRef = useRef<InwardStation[]>([]);
  // Consecutive "not stored yet" answers per bin. A tote that never reports `slotAssigned`
  // is indistinguishable from one the operator never placed, so the only way that state
  // becomes visible is to say so after it has clearly gone on too long.
  const notStoredCountsRef = useRef<Map<string, number>>(new Map());
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const pollingInFlightRef = useRef(false);
  const pendingOrdersPollRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const doneTimersRef = useRef<
    Record<string, ReturnType<typeof setTimeout>>
  >({});
  const previousInwardActiveRef = useRef<boolean | null>(null);
  const skuLookupRequestRef = useRef(0);

  // Exit-prompt bookkeeping. `promptOpenRef` mirrors exitPromptOpen for use inside
  // the inactivity timer, `lastActivityRef` is the last time the operator or the
  // bot did anything, and `prevPendingCountRef` lets us detect the pending → 0 edge.
  const promptOpenRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());
  const prevPendingCountRef = useRef(0);
  const inwardActiveClockRef = useRef(false);

  const isHjiConfig = configName?.trim().toUpperCase() === "HJI";

  // Anything still awaiting the bot (in-flight stores) plus, for HJI, the backlog of
  // pending inward orders. Reaches 0 only when there is genuinely nothing left to do.
  const pendingInwardCount = inFlightCount + pendingOrders.length;

  // Inward that runs in flight inside a fulfillment run is ended by that run, not by the
  // operator: no Exit Inward control, and no prompt offering to exit.
  const exitPromptEnabled = inwardActive && !isInflightInward;

  // The orchestration is winding down: the exit was requested (locally this session or in
  // a previous one) and it has not finished yet — once it does, `inwardActive` goes false
  // and the whole exit footer disappears.
  const exitInProgress = exitingInward || exitRequested;

  // Nothing left to offer once the exit is already on its way.
  const exitPromptAvailable = exitPromptEnabled && !exitRequested;

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const openExitPrompt = useCallback((reason: ExitPromptReason) => {
    setExitPromptReason(reason);
    setExitPromptOpen(true);
    promptOpenRef.current = true;
    playAlertSound();
  }, []);

  const closeExitPrompt = useCallback(() => {
    promptOpenRef.current = false;
    setExitPromptOpen(false);
  }, []);

  const fetchStations = useCallback(async (): Promise<
    InwardStation[] | null
  > => {
    try {
      const res = await axios.get("PickStation/inwardStations", {
        headers: { Accept: "application/json", tenantId: 1 },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setStations(res.data);
        return res.data;
      }
    } catch (error) {
      console.error("Failed to fetch inward stations:", error);
    }
    return null;
  }, []);


  const fetchStorageStatistics = useCallback(async () => {
    try {
      const res = await axios.get("Admin/storage/statistics", {
        headers: { Accept: "application/json", tenantId: 1 },
      });
      if (res.status === 200 && res.data != null) {
        setAvailableForStorage(res.data.availableForStorage ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch storage statistics:", error);
    }
  }, []);

  const fetchPendingInwardOrders = useCallback(
    async (
      showLoading = false,
      options?: {
        throwOnError?: boolean;
      }
    ): Promise<PendingInwardOrder[]> => {
      if (showLoading) {
        setPendingOrdersLoading(true);
      }

      try {
        const res = await axios.get("InwardOrder/pending", {
          headers: { Accept: "application/json", tenantId: 1 },
        });

        if (res.status === 200 && Array.isArray(res.data)) {
          setPendingOrders(res.data);
          setPendingOrdersError(null);

          return res.data;
        }

        setPendingOrders([]);
        setPendingOrdersError("Unable to load pending inward orders.");
        if (options?.throwOnError) {
          throw new Error("Unable to load pending inward orders.");
        }
      } catch (error) {
        console.error("Failed to fetch pending inward orders:", error);
        setPendingOrdersError("Unable to load pending inward orders.");
        if (options?.throwOnError) {
          throw error;
        }
      } finally {
        if (showLoading) {
          setPendingOrdersLoading(false);
        }
      }

      return [];
    },
    []
  );

  const fetchStaticPickWalls = useCallback(async () => {
    try {
      const res = await axios.get("StaticPickWall", {
        headers: { Accept: "application/json", tenantId: 1 },
      });
      if (res.status === 200) {
        setStaticSlots(flattenStaticPickWallSlots(res.data));
      }
    } catch (error) {
      console.error("Failed to fetch static pick walls:", error);
      setStaticSlots([]);
    }
  }, []);

  const lookupSku = useCallback(async (rawSkuId: string) => {
    const trimmedSkuId = rawSkuId.trim();

    if (!trimmedSkuId) {
      setResolvedSku(null);
      setSkuLookupError(null);
      setSkuLookupLoading(false);
      return;
    }

    const requestId = ++skuLookupRequestRef.current;
    setSkuLookupLoading(true);
    setSkuLookupError(null);

    try {
      const res = await axios.get(`MasterData/sku/${encodeURIComponent(trimmedSkuId)}`, {
        headers: { Accept: "application/json", tenantId: 1 },
      });

      if (requestId !== skuLookupRequestRef.current) {
        return;
      }

      if (res.status === 200 && res.data) {
        const inductQuantity =
          typeof res.data?.inductQuantity === "number" && res.data.inductQuantity > 0
            ? String(res.data.inductQuantity)
            : "";

        setResolvedSku({
          skuId: res.data?.skuId ?? trimmedSkuId,
          isStatic: res.data?.isStatic === true,
          inductQuantity,
        });
        setSkuLookupError(null);
        return;
      }

      setResolvedSku(null);
      setSkuLookupError("SKU ID not found.");
    } catch (error: any) {
      if (requestId !== skuLookupRequestRef.current) {
        return;
      }

      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        "Unable to load SKU details.";
      setResolvedSku(null);
      setSkuLookupError(serverMessage);
    } finally {
      if (requestId === skuLookupRequestRef.current) {
        setSkuLookupLoading(false);
      }
    }
  }, []);

  // "Has an exit already been requested" is server state: the orchestrator writes a single
  // "Exit Orchestration" step for the run, and InwardProcessor gates on that same record.
  const fetchExitRequested = useCallback(async () => {
    if (!orchestrationId) return;

    try {
      const res = await OrchestratorAPI.get(
        `orchestrations/${encodeURIComponent(orchestrationId)}/steps`,
        { headers: { Accept: "application/json", tenantId: 1 } }
      );

      // Only a confirmed response may change the answer. A transient failure keeps the
      // button in whatever state the last good read established.
      if (res.status === 200 && Array.isArray(res.data)) {
        setExitRequested(hasExitOrchestrationStep(res.data));
      }
    } catch (error) {
      console.error("Failed to read exit orchestration state:", error);
    }
  }, [orchestrationId]);

  const handleExitInward = async () => {
    if (exitInProgress || !inwardActive) return;

    setExitingInward(true);
    try {
      await OrchestratorAPI.post(
        "orchestrations/exitInward",
        {},
        { headers: { Accept: "application/json", tenantId: 1 } }
      );
      await fetchStations();
      await fetchStorageStatistics();
      await fetchExitRequested();
      const { inwardActive: hasActiveInward } = await refreshInwardStatus();
      message.success(
        hasActiveInward
          ? "Exit requested. Inward will stop after current operations complete."
          : "Inward operation exited successfully."
      );
    } catch (error) {
      console.error("Failed to exit inward:", error);
      setExitingInward(false);
      message.error("Failed to exit inward. Please try again.");
    }
  };

  const handleExitPromptYes = async () => {
    await handleExitInward();
    closeExitPrompt();
  };

  const handleExitPromptNo = () => {
    closeExitPrompt();
    // Restart the inactivity window so the prompt doesn't immediately reappear.
    markActivity();
  };


  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchStations(),
      fetchStaticPickWalls(),
      fetchStorageStatistics(),
    ]).finally(() => setLoading(false));
  }, [fetchStations, fetchStaticPickWalls, fetchStorageStatistics]);

  // The orchestration ended (exit completed, or it finished on its own), so drop the
  // "Exiting Inward..." state the button was left in.
  useEffect(() => {
    if (!inwardActive) {
      setExitingInward(false);
    }
  }, [inwardActive]);

  // A different orchestration is a fresh question.
  useEffect(() => {
    setExitRequested(false);
  }, [orchestrationId]);

  // Polled only until the answer is yes: it cannot go back to no while the orchestration
  // lives, and the steps response carries one step per inward order, so it grows all shift.
  useEffect(() => {
    if (!exitPromptEnabled || !orchestrationId || exitRequested) {
      return;
    }

    fetchExitRequested();
    const interval = setInterval(fetchExitRequested, 5000);

    return () => clearInterval(interval);
  }, [exitPromptEnabled, exitRequested, fetchExitRequested, orchestrationId]);

  useEffect(() => {
    if (inwardActive) {
      fetchStations();
      fetchStorageStatistics();
    } else if (previousInwardActiveRef.current) {
      fetchStations();
      fetchStorageStatistics();
    }

    previousInwardActiveRef.current = inwardActive;
  }, [inwardActive, fetchStations, fetchStorageStatistics]);

  useEffect(() => {
    if (pendingOrdersPollRef.current) {
      clearInterval(pendingOrdersPollRef.current);
      pendingOrdersPollRef.current = null;
    }

    if (!isHjiConfig || !inwardActive) {
      setPendingOrders([]);
      setPendingOrdersError(null);
      setPendingOrdersLoading(false);
      return;
    }

    fetchPendingInwardOrders(true);
    pendingOrdersPollRef.current = setInterval(() => {
      fetchPendingInwardOrders(false);
    }, 5000);

    return () => {
      if (pendingOrdersPollRef.current) {
        clearInterval(pendingOrdersPollRef.current);
        pendingOrdersPollRef.current = null;
      }
    };
  }, [inwardActive, fetchPendingInwardOrders, isHjiConfig]);

  useEffect(() => {
    if (staticSlots.length === 0) {
      skuLookupRequestRef.current += 1;
      setResolvedSku(null);
      setSkuLookupError(null);
      setSkuLookupLoading(false);
      return;
    }

    const trimmedSkuId = scanSkuId.trim();

    if (!trimmedSkuId) {
      skuLookupRequestRef.current += 1;
      setResolvedSku(null);
      setSkuLookupError(null);
      setSkuLookupLoading(false);
      return;
    }

    const timer = setTimeout(() => {
      lookupSku(trimmedSkuId);
    }, 400);

    return () => clearTimeout(timer);
  }, [lookupSku, scanSkuId, staticSlots.length]);

  const dynamicGates = useMemo(
    () =>
      stations.flatMap((station) =>
        station.gates.map((gate) => ({
          ...gate,
          kind: "dynamic" as const,
        }))
      ),
    [stations]
  );

  const staticGates = useMemo(
    () =>
      staticSlots.map((slot) => ({
        id: slot.id,
        slot: {
          x: slot.x,
          y: slot.y,
          z: slot.z,
        },
        processingQueue: [] as ProcessingQueueItem[],
        group: "StaticPickWall",
        mappedPtlDeviceId: slot.mappedPtlDeviceId ?? "",
        isDisabled: slot.isDisabled,
        kind: "static" as const,
      })),
    [staticSlots]
  );

  const regularRows = useMemo(() => {
    const zValues = [...new Set(dynamicGates.map((gate) => gate.slot.z))].sort(
      (left, right) => right - left
    );

    return zValues.map((z) =>
      dynamicGates
        .filter((gate) => gate.slot.z === z)
        .sort((left, right) => right.slot.x - left.slot.x)
    );
  }, [dynamicGates]);

  const staticRows = useMemo(() => {
    const zValues = [...new Set(staticGates.map((gate) => gate.slot.z))].sort(
      (left, right) => right - left
    );

    return zValues.map((z) =>
      staticGates
        .filter((gate) => gate.slot.z === z)
        .sort((left, right) => right.slot.x - left.slot.x)
    );
  }, [staticGates]);

  const requiresSkuRouting = staticGates.length > 0;

  const getSlotState = useCallback(
    (gate: Gate): SlotVisualState | "disabled" => {
      if (gate.isDisabled) {
        return "disabled";
      }

      if (!inwardActive) {
        return "disabled";
      }

      const key = slotKey(gate.slot);
      const override = localOverrides[key];
      if (override) {
        return override;
      }

      if (gate.kind === "static") {
        if (!requiresSkuRouting || resolvedSku?.isStatic !== true) {
          return "disabled";
        }

        return "enabled";
      }

      if (requiresSkuRouting && resolvedSku?.isStatic !== false) {
        return "disabled";
      }

      if (gate.processingQueue && gate.processingQueue.length > 0) {
        return "processing";
      }

      return "enabled";
    },
    [inwardActive, localOverrides, requiresSkuRouting, resolvedSku?.isStatic]
  );


  const flashSlotDone = useCallback((key: string) => {
    setLocalOverrides((prev) => ({ ...prev, [key]: "done" }));

    doneTimersRef.current[key] = setTimeout(() => {
      setLocalOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      delete doneTimersRef.current[key];
    }, 3000);
  }, []);

  const clearSlotOverride = useCallback((key: string) => {
    setLocalOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isBinStored = useCallback(async (binId: string): Promise<boolean> => {
    try {
      const response = await axios.get(
        `BinMaster/getById/${encodeURIComponent(binId)}`,
        { headers: { Accept: "application/json", tenantId: 1 } }
      );

      const stored = response.data?.slotAssigned === true;

      if (stored) {
        notStoredCountsRef.current.delete(binId);
        return true;
      }
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }
    }

    // Both "bin exists but is not in the grid" and "no bin row yet" land here.
    const misses = (notStoredCountsRef.current.get(binId) ?? 0) + 1;
    notStoredCountsRef.current.set(binId, misses);

    // Warned once at the threshold rather than on every pass.
    if (misses === NOT_STORED_WARN_THRESHOLD) {
      console.warn(
        `[material-inward] bin ${binId} has reported slotAssigned=false/404 ` +
        `${misses} times in a row; its inward task cannot be completed until the ` +
        `tote is actually stored.`
      );
    }

    return false;
  }, []);

  const checkCompletions = useCallback(
    async (data: InwardStation[]) => {
      if (data.length === 0) return;

      const allGates = data.flatMap((s) => s.gates);
      const completedSlots: Array<{
        binId: string;
        slot: MonitoredInwardSlot;
      }> = [];
      // Totes absent from the gate queue that this poll never caught sitting there. Only the
      // server can say whether they were stored, so they are settled by asking, below.
      const unverifiedSlots: Array<{
        binId: string;
        slot: MonitoredInwardSlot;
      }> = [];

      monitoredSlotsRef.current.forEach((slot, monitoredBinId) => {
        const gate = allGates.find((g) => slotKey(g.slot) === slot.slotKey);
        const queue = gate?.processingQueue ?? [];
        const matchingQueueItem = queue.find(
          (item) => normalizeBinId(item.binId) === monitoredBinId
        );

        if (matchingQueueItem) {
          if (matchingQueueItem.operatorTaskId) {
            slot.operatorTaskId = matchingQueueItem.operatorTaskId;
          }
          slot.hasSeenProcessing = true;
          return;
        }

        if (slot.completing) {
          return;
        }

        // The gate having moved on to another tote is not a reason to wait: this tote is
        // matched by bin id, so its absence from the queue means the bot has taken it.
        if (slot.hasSeenProcessing) {
          completedSlots.push({ binId: monitoredBinId, slot });
          return;
        }

        // Never observed at the gate — which is not evidence the tote is still there. A gate
        // visit lasts from the PLC request to the ReleaseBin ack, about 2.5s under EdgeSim's
        // 1-2s delays, so a 3s poll can straddle it end to end and never see the tote at all.
        // This branch used to `return`, leaving the slot stuck for the life of the mount and
        // its operator task pending forever. Ask the server instead of depending on having
        // caught a transient state.
        unverifiedSlots.push({ binId: monitoredBinId, slot });
      });

      if (unverifiedSlots.length > 0) {
        // A tote leaves the queue on the ReleaseBin ack but is only marked stored on the
        // StoreBin ack a few seconds later, so "neither at the gate nor stored" is a normal
        // in-between state. Anything not confirmed stored simply stays monitored for the
        // next poll — completion still happens only on positive server confirmation.
        const verdicts = await Promise.all(
          unverifiedSlots.map(async ({ binId, slot }) => {
            try {
              return { binId, slot, stored: await isBinStored(binId) };
            } catch (error) {
              // A failed check is not a negative. Leave it for the next poll.
              console.error(
                `Failed to confirm storage for inward bin ${binId}:`,
                error
              );
              return { binId, slot, stored: false };
            }
          })
        );

        verdicts.forEach(({ binId, slot, stored }) => {
          if (stored) {
            completedSlots.push({ binId, slot });
          }
        });
      }

      if (completedSlots.length === 0) return;

      // The store is done — either the bin was seen at the gate and has now left the queue,
      // or the server confirmed it slot-assigned. Resolve each bin's operatorTaskId from the
      // inward order and retry on later polls if the id is not available yet.
      let pending: PendingInwardOrder[];
      try {
        pending = await fetchPendingInwardOrders(false, {
          throwOnError: true,
        });
      } catch (_error) {
        return;
      }

      const binToTaskId = new Map<string, string>();
      pending.forEach((order) => {
        const item = order.inwardOrderItem;
        if (item?.binId && item?.operatorTaskId) {
          binToTaskId.set(normalizeBinId(item.binId), item.operatorTaskId);
        }
      });

      await Promise.all(
        completedSlots.map(async ({ binId, slot }) => {
          const operatorTaskId =
            binToTaskId.get(binId) ?? slot.operatorTaskId;

          if (!operatorTaskId) {
            return;
          }

          slot.completing = true;

          try {
            await axios.put(
              `PickStation/completeInwardTask/${encodeURIComponent(operatorTaskId)}`,
              undefined,
              {
                headers: { Accept: "application/json", tenantId: 1 },
              }
            );
          } catch (error: any) {
            if (!isAlreadyCompleteError(error)) {
              slot.completing = false;
              console.error(
                `Failed to complete inward task ${operatorTaskId}:`,
                error
              );
              return;
            }
          }

          monitoredSlotsRef.current.delete(binId);
          flashSlotDone(slot.slotKey);
        })
      );

      setInFlightCount(monitoredSlotsRef.current.size);

      if (
        monitoredSlotsRef.current.size === 0 &&
        pollIntervalRef.current
      ) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    },
    [fetchPendingInwardOrders, flashSlotDone, isBinStored]
  );

  const ensurePolling = useCallback(() => {
    if (pollIntervalRef.current) return;

    pollIntervalRef.current = setInterval(async () => {
      if (pollingInFlightRef.current) return;

      pollingInFlightRef.current = true;
      try {
        const res = await axios.get(
          "PickStation/inwardStations",
          {
            headers: {
              Accept: "application/json",
              tenantId: 1,
            },
          }
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          setStations(res.data);
          await checkCompletions(res.data);
        }
      } catch (error) {
        console.error("Polling error:", error);
      } finally {
        pollingInFlightRef.current = false;
      }
    }, 3000);
  }, [checkCompletions]);

  useEffect(() => {
    stationsRef.current = stations;
  }, [stations]);

  useEffect(() => {
    if (stations.length === 0) return;

    // Recovery path (e.g. the page was refreshed mid-operation): adopt any gate that
    // is already processing but isn't being monitored yet, keyed by the bin sitting
    // at that gate. The binId comes straight from the processing queue, so its
    // operatorTaskId can still be resolved from the inward order once the gate clears.
    let needsPolling = false;
    stations.flatMap((s) => s.gates).forEach((gate) => {
      const key = slotKey(gate.slot);
      if (
        gate.processingQueue &&
        gate.processingQueue.length > 0
      ) {
        const observedBinId = normalizeBinId(gate.processingQueue[0]?.binId);
        if (observedBinId && !monitoredSlotsRef.current.has(observedBinId)) {
          monitoredSlotsRef.current.set(observedBinId, {
            binId: observedBinId,
            slotKey: key,
            operatorTaskId: gate.processingQueue[0]?.operatorTaskId,
            hasSeenProcessing: true,
            completing: false,
          });
          needsPolling = true;
        }
      }
    });

    if (needsPolling) {
      ensurePolling();
    }

    setInFlightCount(monitoredSlotsRef.current.size);
  }, [stations, ensurePolling]);

  // Completions the live monitor above cannot make. It only knows about totes submitted during
  // this mount, so anything still in flight when the operator switches to another screen is
  // invisible to it after the remount — the task then stays pending for the rest of the shift
  // and the orchestration step behind it never finishes.
  //
  // App already polls PickStation/inwardTasks for the badge count and hands it down as
  // `taskDetails`, so the authoritative list costs nothing to re-check here: in the normal case
  // this is pure computation over data already in hand and issues no requests at all.
  const reconcileStrandedInwardTasks = useCallback(
    async (tasks: any) => {
      if (reconcilingRef.current) return;

      // Bins the live monitor is mid-PUT on are its own; everything else is fair game, including
      // a tote it is still watching, since the storage check below is the stricter test anyway.
      const binsBeingCompleted = [...monitoredSlotsRef.current.values()]
        .filter((slot) => slot.completing)
        .map((slot) => slot.binId);

      const options = {
        ignoreBinIds: binsBeingCompleted,
        ignoreTaskIds: reconcileInFlightRef.current,
      };

      if (findStrandedInwardTasks(tasks, stationsRef.current, options).length === 0) {
        return;
      }

      reconcilingRef.current = true;
      try {
        // The snapshot is only refreshed while the live monitor is polling, so re-read it before
        // acting on it: a stale one shows an empty gate for a tote that is still sitting there.
        const freshStations = await fetchStations();
        const stranded = findStrandedInwardTasks(
          tasks,
          freshStations ?? stationsRef.current,
          options
        );

        let completed = 0;

        for (const { operatorTaskId, binId } of stranded) {
          reconcileInFlightRef.current.add(operatorTaskId);

          try {
            // Absence from the gate is not proof the store happened — the tote could be in
            // transit, or never have been placed at all. `slotAssigned` is the server
            // confirming it is in the grid, which is what completion actually attests to.
            if (!(await isBinStored(binId))) {
              continue;
            }

            await axios.put(
              `PickStation/completeInwardTask/${encodeURIComponent(operatorTaskId)}`,
              undefined,
              { headers: { Accept: "application/json", tenantId: 1 } }
            );

            completed += 1;
            console.info(
              `Completed stranded inward task ${operatorTaskId} for bin ${binId}.`
            );
          } catch (error: any) {
            if (isAlreadyCompleteError(error)) {
              completed += 1;
              continue;
            }

            // "Not stored yet" is now answered by `isBinStored` returning false, so anything
            // reaching here — including a 404 from the PUT, meaning the task id itself is
            // unknown — is a real failure and is reported rather than retried in silence.
            console.error(
              `Failed to complete stranded inward task ${operatorTaskId}:`,
              error
            );
          } finally {
            reconcileInFlightRef.current.delete(operatorTaskId);
          }
        }

        if (completed > 0) {
          fetchStorageStatistics();
          fetchPendingInwardOrders(false);
        }
      } finally {
        reconcilingRef.current = false;
      }
    },
    [fetchPendingInwardOrders, fetchStations, fetchStorageStatistics, isBinStored]
  );

  // Driven by App's inward-task poll (every REACT_APP_REFRESH_DURATION), not by the station
  // snapshot, so the sweep cannot retrigger itself through its own `fetchStations` call.
  useEffect(() => {
    reconcileStrandedInwardTasks(taskDetails);
  }, [taskDetails, reconcileStrandedInwardTasks]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current)
        clearInterval(pollIntervalRef.current);
      if (pendingOrdersPollRef.current)
        clearInterval(pendingOrdersPollRef.current);
      Object.values(doneTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Reset the activity clock only on a genuine inactive → active transition (so a
  // transient re-assert of inwardActive by the orchestration poll can't keep pushing
  // the idle deadline out). Auto-close the prompt if inward ends.
  useEffect(() => {
    const wasActive = inwardActiveClockRef.current;
    inwardActiveClockRef.current = inwardActive;

    if (inwardActive && !wasActive) {
      markActivity();
    }

    if (!exitPromptAvailable && promptOpenRef.current) {
      closeExitPrompt();
    }
  }, [inwardActive, exitPromptAvailable, markActivity, closeExitPrompt]);

  // Trigger 1: the last pending inward was completed (pending count fell to 0).
  useEffect(() => {
    const previous = prevPendingCountRef.current;
    prevPendingCountRef.current = pendingInwardCount;

    // Any change in the pending count means the station is still active.
    if (previous !== pendingInwardCount) {
      markActivity();
    }

    const reason = evaluateExitPrompt({
      inwardActive: exitPromptAvailable,
      promptOpen: exitPromptOpen,
      previousPendingCount: previous,
      currentPendingCount: pendingInwardCount,
      inactivityElapsedMs: 0,
    });

    if (reason) {
      openExitPrompt(reason);
    }
  }, [
    pendingInwardCount,
    exitPromptAvailable,
    exitPromptOpen,
    markActivity,
    openExitPrompt,
  ]);

  // Trigger 2: no inward activity for longer than the inactivity limit.
  useEffect(() => {
    if (!exitPromptAvailable) return;

    const interval = setInterval(() => {
      if (promptOpenRef.current) return;

      const idleMs = Date.now() - lastActivityRef.current;

      if (process.env.NODE_ENV !== "production") {
        console.debug(
          `[material-inward] idle ${Math.round(idleMs / 1000)}s of ${INWARD_INACTIVITY_LIMIT_MS / 1000
          }s`
        );
      }

      const reason = evaluateExitPrompt({
        inwardActive: true,
        promptOpen: promptOpenRef.current,
        // Pass equal counts so only the inactivity branch can fire here.
        previousPendingCount: prevPendingCountRef.current,
        currentPendingCount: prevPendingCountRef.current,
        inactivityElapsedMs: idleMs,
      });

      if (reason) {
        openExitPrompt(reason);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [exitPromptAvailable, openExitPrompt]);


  const handleSlotClick = (gate: Gate) => {
    if (gate.isDisabled) return;
    markActivity();
    const state = getSlotState(gate);
    if (state === "enabled") {
      if (isHjiConfig && pendingOrders.length === 0) {
        if (pendingOrdersError) {
          message.error(pendingOrdersError);
        } else if (pendingOrdersLoading) {
          message.info("Pending inward orders are still loading.");
        } else {
          message.info("No pending inward orders are available.");
        }
        return;
      }
      setSelectedGate(gate);
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    if (!submitting) {
      setIsModalOpen(false);
      setSelectedGate(null);
      setSubmitError(null);
    }
  };

  const updateSubmittedSlotState = (
    key: string,
    isStaticSlot: boolean,
    binId: string
  ) => {
    if (isStaticSlot) {
      setLocalOverrides((prev) => ({
        ...prev,
        [key]: "done",
      }));

      doneTimersRef.current[key] = setTimeout(() => {
        setLocalOverrides((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        delete doneTimersRef.current[key];
      }, 3000);
      return;
    }

    setLocalOverrides((prev) => ({
      ...prev,
      [key]: "processing",
    }));
    // Monitor by binId. checkCompletions resolves the operatorTaskId from the inward
    // order once this bin leaves the gate's processingQueue (bin stored).
    monitoredSlotsRef.current.set(normalizeBinId(binId), {
      binId: normalizeBinId(binId),
      slotKey: key,
      hasSeenProcessing: false,
      completing: false,
    });
    ensurePolling();
    setInFlightCount(monitoredSlotsRef.current.size);
  };

  const handleLegacySubmit = async (
    containerId: string,
    skuId: string,
    unitCount: number,
    expirationDate: string
  ) => {
    if (!selectedGate) return;

    markActivity();
    setSubmitting(true);
    setSubmitError(null);
    const key = slotKey(selectedGate.slot);
    const isStaticInward = selectedGate.kind === "static";

    try {
      let binId = containerId;

      if (isStaticInward) {
        const slotResponse = await axios.get(
          `Slot/slot/${selectedGate.slot.x}/${selectedGate.slot.y}/${selectedGate.slot.z}`,
          {
            headers: { Accept: "application/json", tenantId: 1 },
          }
        );

        const assignedBinId =
          slotResponse.data?.binId ??
          slotResponse.data?.BinId ??
          slotResponse.data?.bin ??
          slotResponse.data?.Bin;

        if (typeof assignedBinId !== "string" || assignedBinId.trim() === "") {
          throw new Error("No bin is assigned to the selected static slot.");
        }

        binId = assignedBinId.trim();
      }

      await axios.post("InwardOrder", {
        inwardOrderItem:
        {
          from: {
            x: selectedGate.slot.x,
            y: selectedGate.slot.y,
            z: selectedGate.slot.z,
          },
          inwardOrderSkus: [
            {
              skuId,
              quantity: unitCount,
              partition: "0",
              inwardedQuantity: 0,
              ...(isStaticInward ? { caseId: containerId } : {}),
              ...(expirationDate ? { expirationDate } : {}),
            },
          ],
          binId,
          remarks: "",
          inwardRemarks: "",
          stagingDateTimeUtc: new Date().toISOString(),
          completionState: 0,
          completionDateTimeUtc: new Date().toISOString(),
        },
        isTestOrder: false,
      });

      updateSubmittedSlotState(key, isStaticInward, binId);
      fetchStorageStatistics();

      setIsModalOpen(false);
      setSelectedGate(null);
    } catch (error: any) {
      console.error("Failed to create inward order:", error);
      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        error?.message ||
        "Failed to create inward order. Please try again.";
      setSubmitError(serverMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Matches a pending inward operator task to a submitted static placement by bin + SKU.
  const inwardTaskMatchesBinSku = (
    task: any,
    binId: string,
    skuId: string
  ): boolean => {
    const inwardTask = task?.inwardTask;
    if (!inwardTask) {
      return false;
    }

    if (normalizeBinId(inwardTask.binId) !== normalizeBinId(binId)) {
      return false;
    }

    const trimmedSkuId = skuId.trim();
    return (inwardTask.binSkus ?? []).some(
      (binSku: any) => binSku?.skuId?.trim() === trimmedSkuId
    );
  };


  const resolveStaticInwardTaskId = async (
    binId: string,
    skuId: string
  ): Promise<string | undefined> => {
    const maxAttempts = 20;
    const retryDelayMs = 1000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const res = await axios.get("PickStation/inwardTasks", {
          headers: { Accept: "application/json", tenantId: 1 },
        });

        if (Array.isArray(res.data)) {
          const match = res.data.find((task) =>
            inwardTaskMatchesBinSku(task, binId, skuId)
          );
          if (match?.id) {
            return match.id as string;
          }
        }
      } catch (error) {
        console.error("Failed to fetch pending inward tasks:", error);
      }

      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }

    return undefined;
  };


  const completeStaticInwardTask = async (
    key: string,
    binId: string,
    skuId: string
  ) => {
    const operatorTaskId = await resolveStaticInwardTaskId(binId, skuId);

    if (!operatorTaskId) {
      console.error(
        `Could not resolve an inward task to complete for bin ${binId} / SKU ${skuId}.`
      );
      message.error(
        "Could not auto-complete the static inward task. Please verify it in admin."
      );
      clearSlotOverride(key);
      return;
    }

    try {
      await axios.put(
        `PickStation/completeInwardTask/${encodeURIComponent(operatorTaskId)}`,
        undefined,
        {
          headers: { Accept: "application/json", tenantId: 1 },
        }
      );
    } catch (error: any) {
      if (!isAlreadyCompleteError(error)) {
        console.error(
          `Failed to complete static inward task ${operatorTaskId}:`,
          error
        );
        message.error("Failed to complete the static inward task.");
        clearSlotOverride(key);
        return;
      }
    }

    flashSlotDone(key);
  };

  const handleHjiSubmit = async (submission: InwardBinLocationSubmission) => {
    if (!selectedGate) return;

    markActivity();
    setSubmitting(true);
    setSubmitError(null);
    const key = slotKey(selectedGate.slot);
    const isStaticInward = selectedGate.kind === "static";

    try {
      await axios.put(
        `InwardOrder/${encodeURIComponent(
          submission.inwardOrderReferenceId
        )}/binLocation`,
        {
          binId: submission.toteId,
          fromLocation: {
            x: selectedGate.slot.x,
            y: selectedGate.slot.y,
            z: selectedGate.slot.z,
          },
          skuId: submission.skuId,
          caseId: submission.caseId,
          inwardedQuantity: submission.inwardedQuantity,
          ...(submission.expirationDate
            ? { expirationDate: submission.expirationDate }
            : {}),
        },
        {
          headers: { Accept: "application/json", tenantId: 1 },
        }
      );

      if (isStaticInward) {
        setLocalOverrides((prev) => ({ ...prev, [key]: "processing" }));
        void completeStaticInwardTask(key, submission.toteId, submission.skuId);
      } else {
        updateSubmittedSlotState(key, false, submission.toteId);
      }
      fetchStorageStatistics();
      setPendingOrders((prev) =>
        prev.filter(
          (order) =>
            order.inwardOrderReferenceId !==
            submission.inwardOrderReferenceId
        )
      );
      fetchPendingInwardOrders(false);
      skuLookupRequestRef.current += 1;
      setScanSkuId("");
      setResolvedSku(null);
      setSkuLookupError(null);
      setSkuLookupLoading(false);

      setIsModalOpen(false);
      setSelectedGate(null);
    } catch (error: any) {
      console.error("Failed to update inward order bin location:", error);
      const serverMessage =
        error?.response?.data?.toString?.() ||
        error?.response?.data?.title ||
        error?.message ||
        "Failed to update inward order bin location. Please try again.";
      setSubmitError(serverMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const scanStatus = useMemo(() => {
    if (!requiresSkuRouting) {
      return null;
    }

    if (skuLookupLoading) {
      return {
        message: "Looking up SKU routing...",
        isError: false,
      };
    }

    if (skuLookupError) {
      return {
        message: skuLookupError,
        isError: true,
      };
    }

    if (resolvedSku) {
      return {
        message: resolvedSku.isStatic
          ? "Static SKU detected. Only static pickwall slots are enabled."
          : "Dynamic SKU detected. Only standard inward slots are enabled.",
        isError: false,
      };
    }

    return {
      message: "Scan or finish typing a SKU ID to enable the correct slots.",
      isError: false,
    };
  }, [requiresSkuRouting, resolvedSku, skuLookupError, skuLookupLoading]);

  const renderSlot = (gate: Gate) => {
    const state = getSlotState(gate);
    const key = slotKey(gate.slot);

    return (
      <Styles.SlotCard
        key={key}
        state={state}
        onClick={() => handleSlotClick(gate)}
      >
        <div className="slot-content">
          {state === "processing" && (
            <div className="processing-indicator">
              <Styles.Spinner />
              <span>Processing...</span>
            </div>
          )}
          {state === "done" && (
            <div className="done-indicator">
              <span className="tick">&#10003;</span>
            </div>
          )}
          {state === "enabled" && (
            <div className="enabled-indicator">
              <span>Available</span>
            </div>
          )}
          {state === "disabled" && (
            <div className="disabled-indicator">
              <span>Disabled</span>
            </div>
          )}
        </div>
        <div className="slot-footer">
          Bin - {formatSlotDisplay(gate.slot)}
        </div>
      </Styles.SlotCard>
    );
  };


  const exitInwardFooter = exitPromptEnabled ? (
    <Styles.ExitFooter>
      <Styles.ExitInwardButton
        onClick={handleExitInward}
        disabled={exitInProgress}
      >
        {exitInProgress ? "Exiting Inward..." : "Exit Inward"}
      </Styles.ExitInwardButton>
    </Styles.ExitFooter>
  ) : null;

  const exitInwardPrompt = (
    <Modal
      open={exitPromptOpen}
      title="Exit Inward"
      onOk={handleExitPromptYes}
      onCancel={handleExitPromptNo}
      okText="Yes"
      cancelText="No"
      centered
      maskClosable={false}
      okButtonProps={{ loading: exitInProgress }}
      cancelButtonProps={{ disabled: exitInProgress }}
    >
      <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        Would you like to exit inward?
      </p>
      {exitPromptReason && (
        <p style={{ color: "#888", margin: 0 }}>
          {exitPromptReason === "inactivity"
            ? "There has been no inward activity for 5 minutes."
            : "All pending inwards have been completed."}
        </p>
      )}
    </Modal>
  );

  if (configName === undefined || loading || orchestratorLoading) {
    return (
      <Styles.PageContainer>
        <Styles.LoadingContainer>
          <Styles.LargeSpinner />
          <span>Loading inward stations...</span>
        </Styles.LoadingContainer>
      </Styles.PageContainer>
    );
  }

  if (dynamicGates.length === 0 && staticGates.length === 0) {
    return (
      <Styles.PageContainer>
        <Styles.PageHeader>
          <Styles.PageTitle>
            Select Slot for Tote Inwarding
          </Styles.PageTitle>
        </Styles.PageHeader>
        <Styles.EmptyStateContainer>
          <span>No inward slots configured.</span>
        </Styles.EmptyStateContainer>
        {exitInwardFooter}
        {exitInwardPrompt}
      </Styles.PageContainer>
    );
  }

  return (
    <Styles.PageContainer>
      <Styles.PageHeader>
        <Styles.PageTitle>
          Select Slot for Tote Inwarding
        </Styles.PageTitle>
        <Styles.HeaderRight>
          <Styles.StorageCount>
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <rect
                x="1"
                y="1"
                width="16"
                height="16"
                rx="2"
                stroke="#444"
                strokeWidth="1.5"
              />
              <line
                x1="9"
                y1="1"
                x2="9"
                y2="17"
                stroke="#444"
                strokeWidth="1"
              />
              <line
                x1="1"
                y1="9"
                x2="17"
                y2="9"
                stroke="#444"
                strokeWidth="1"
              />
            </svg>
            <span>
              Number of available storage locations:{" "}
              <strong>{availableForStorage}</strong>
            </span>
          </Styles.StorageCount>
        </Styles.HeaderRight>
      </Styles.PageHeader>

      {isHjiConfig && inwardActive && (
        <Styles.PendingOrdersBanner $error={Boolean(pendingOrdersError)}>
          {pendingOrdersError
            ? pendingOrdersError
            : pendingOrdersLoading
              ? "Checking for pending inward orders..."
              : pendingOrders.length > 0
                ? `${pendingOrders.length} pending inward order${pendingOrders.length === 1 ? "" : "s"
                } available. Click any enabled slot to select and fulfill an order.`
                : "No pending inward orders are available right now."}
        </Styles.PendingOrdersBanner>
      )}

      <Styles.GridContainer>
        {requiresSkuRouting && (
          <Styles.ScanSkuCard>
            <Styles.ScanSkuHeading>
              <h3>Scan SKU ID</h3>
              <span>
                Press <strong>Enter</strong> after scanning, or pause typing to auto-detect the route.
              </span>
            </Styles.ScanSkuHeading>
            <Styles.ScanSkuInputRow>
              <Input
                placeholder="Scan or enter SKU ID"
                value={scanSkuId}
                onChange={(event) => {
                  markActivity();
                  setScanSkuId(event.target.value);
                  setSubmitError(null);
                }}
                onPressEnter={() => lookupSku(scanSkuId)}
              />
            </Styles.ScanSkuInputRow>
            {scanStatus && (
              <Styles.ScanStatus $error={scanStatus.isError}>
                {scanStatus.message}
              </Styles.ScanStatus>
            )}
          </Styles.ScanSkuCard>
        )}

        {regularRows.length > 0 && (
          <Styles.SlotsSection>
            <Styles.SectionTitle>Slots</Styles.SectionTitle>
            <Styles.SlotsContainer>
              {regularRows.map((rowGates, rowIndex) => (
                <Styles.SlotsRow key={rowIndex}>
                  {rowGates.map((gate) => renderSlot(gate))}
                </Styles.SlotsRow>
              ))}
            </Styles.SlotsContainer>
          </Styles.SlotsSection>
        )}

        {staticRows.length > 0 && (
          <Styles.SlotsSection>
            <Styles.SectionTitle>Static Pickwall</Styles.SectionTitle>
            <Styles.SlotsContainer>
              {staticRows.map((rowGates, rowIndex) => (
                <Styles.SlotsRow key={`static-${rowIndex}`}>
                  {rowGates.map((gate) => renderSlot(gate))}
                </Styles.SlotsRow>
              ))}
            </Styles.SlotsContainer>
          </Styles.SlotsSection>
        )}
      </Styles.GridContainer>

      <Modal
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={960}
        centered
        closable
        destroyOnClose
        styles={{
          content: {
            border: "4px solid #4d9a9a",
            boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.12)",
            borderRadius: "12px",
            padding: "20px 24px",
          },
        }}
      >
        {selectedGate && isHjiConfig ? (
          <InwardModal
            gate={selectedGate}
            onSubmit={handleHjiSubmit}
            submitting={submitting}
            errorMessage={submitError}
            pendingOrders={pendingOrders}
            pendingOrdersLoading={pendingOrdersLoading}
            pendingOrdersError={pendingOrdersError}
            initialSkuId={requiresSkuRouting ? resolvedSku?.skuId ?? scanSkuId.trim() : ""}
          />
        ) : selectedGate ? (
          <LegacyInwardModal
            gate={selectedGate}
            onSubmit={handleLegacySubmit}
            submitting={submitting}
            errorMessage={submitError}
            initialSkuId={requiresSkuRouting ? resolvedSku?.skuId : undefined}
            initialUnitCount={
              requiresSkuRouting ? resolvedSku?.inductQuantity : undefined
            }
            isStaticSku={selectedGate.kind === "static"}
            lockSkuId={requiresSkuRouting && Boolean(resolvedSku)}
            lockUnitCount={requiresSkuRouting && Boolean(resolvedSku?.inductQuantity)}
          />
        ) : null}
      </Modal>

      {exitInwardFooter}
      {exitInwardPrompt}
    </Styles.PageContainer>
  );
}
